import React, { useState, useEffect } from 'react';
import { 
  getTemplatesByCategory, 
  searchTemplates, 
  EQUIPMENT_QUICK_TEMPLATES 
} from '../data/formTemplates';
import './TemplateSelector.css';

const TemplateSelector = ({ isOpen, onClose, onApplyTemplate, currentEquipment }) => {
  const [activeTab, setActiveTab] = useState('categories');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const categories = getTemplatesByCategory();

  // Recherche en temps réel
  useEffect(() => {
    if (searchQuery.length >= 2) {
      const results = searchTemplates(searchQuery);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const handleApplyTemplate = (template) => {
    onApplyTemplate(template.data);
    onClose();
  };

  const handleQuickTemplate = (problemText) => {
    const quickData = {
      probleme_initial: problemText,
      phenomene_produit: `Problème détecté: ${problemText}`,
      priorite: 'normale',
      cout_estime: '500'
    };
    onApplyTemplate(quickData);
    onClose();
  };

  const getEquipmentFromName = (name) => {
    return name.split(' - ')[1] || name.split(' ')[1] || '';
  };

  const currentEquipmentName = getEquipmentFromName(currentEquipment || '');
  const quickTemplates = EQUIPMENT_QUICK_TEMPLATES[currentEquipmentName] || [];

  if (!isOpen) return null;

  return (
    <div className="template-modal-overlay" onClick={onClose}>
      <div className="template-modal" onClick={(e) => e.stopPropagation()}>
        <div className="template-header">
          <h3>📋 Templates de Formulaires</h3>
          <button className="template-close" onClick={onClose}>×</button>
        </div>

        <div className="template-tabs">
          <button 
            className={`template-tab ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            📂 Par Catégorie
          </button>
          <button 
            className={`template-tab ${activeTab === 'search' ? 'active' : ''}`}
            onClick={() => setActiveTab('search')}
          >
            🔍 Recherche
          </button>
          {quickTemplates.length > 0 && (
            <button 
              className={`template-tab ${activeTab === 'quick' ? 'active' : ''}`}
              onClick={() => setActiveTab('quick')}
            >
              ⚡ Rapide ({currentEquipmentName})
            </button>
          )}
        </div>

        <div className="template-content">
          {/* Onglet Catégories */}
          {activeTab === 'categories' && (
            <div className="template-categories">
              {!selectedCategory ? (
                <div className="category-grid">
                  {categories.map((category, index) => (
                    <div 
                      key={index}
                      className="category-card"
                      onClick={() => setSelectedCategory(category)}
                    >
                      <div className="category-icon">
                        {category.category.split(' ')[0]}
                      </div>
                      <h4>{category.category}</h4>
                      <p>{category.templates.length} template(s)</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="template-list">
                  <div className="category-header">
                    <button 
                      className="back-button"
                      onClick={() => setSelectedCategory(null)}
                    >
                      ← Retour
                    </button>
                    <h4>{selectedCategory.category}</h4>
                  </div>
                  
                  {selectedCategory.templates.map((template) => (
                    <div key={template.id} className="template-item">
                      <div className="template-icon">{template.icon}</div>
                      <div className="template-info">
                        <h5>{template.name}</h5>
                        <p>{template.description}</p>
                        <div className="template-preview">
                          <strong>Problème :</strong> {template.data.probleme_initial}
                        </div>
                      </div>
                      <button 
                        className="template-apply"
                        onClick={() => handleApplyTemplate(template)}
                      >
                        Appliquer
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Onglet Recherche */}
          {activeTab === 'search' && (
            <div className="template-search">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Rechercher un template... (ex: moteur, étiquette, remplissage)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
              
              {searchQuery.length >= 2 ? (
                <div className="search-results">
                  {searchResults.length > 0 ? (
                    searchResults.map((template) => (
                      <div key={template.id} className="template-item">
                        <div className="template-icon">{template.icon}</div>
                        <div className="template-info">
                          <h5>{template.name}</h5>
                          <span className="template-category">{template.category}</span>
                          <p>{template.description}</p>
                        </div>
                        <button 
                          className="template-apply"
                          onClick={() => handleApplyTemplate(template)}
                        >
                          Appliquer
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="no-results">
                      <p>🔍 Aucun template trouvé pour "{searchQuery}"</p>
                      <p>Essayez avec d'autres mots-clés comme : moteur, étiquette, remplissage, bourrage...</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="search-help">
                  <h4>💡 Suggestions de recherche :</h4>
                  <div className="search-suggestions">
                    {['moteur', 'étiquette', 'remplissage', 'bourrage', 'formation'].map(suggestion => (
                      <button 
                        key={suggestion}
                        className="suggestion-tag"
                        onClick={() => setSearchQuery(suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Onglet Templates Rapides */}
          {activeTab === 'quick' && quickTemplates.length > 0 && (
            <div className="quick-templates">
              <div className="quick-header">
                <h4>⚡ Templates rapides pour {currentEquipmentName}</h4>
                <p>Cliquez sur un problème fréquent pour l'appliquer rapidement</p>
              </div>
              
              <div className="quick-grid">
                {quickTemplates.map((problem, index) => (
                  <button
                    key={index}
                    className="quick-template-btn"
                    onClick={() => handleQuickTemplate(problem)}
                  >
                    <span className="quick-icon">⚡</span>
                    {problem}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="template-footer">
          <p>💡 <strong>Astuce :</strong> Les templates pré-remplissent automatiquement les champs pertinents. Vous pouvez les modifier après application.</p>
        </div>
      </div>
    </div>
  );
};

export default TemplateSelector;


